import { VehiclePhotosStrip } from "./RentalFleetExtras";
import { useEffect, useState } from "react";
import { isActiveEmp } from "../../lib/constants";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { api } from "../../lib/api";
import { CustomerDialog } from "./RentalCustomersTab";
import { RentalFilesField } from "./RentalFiles";

const PERIODS = [["daily", "يومي"], ["weekly", "أسبوعي"], ["monthly", "شهري"]];
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };
const EMPTY = { fleet_id: "", renter_type: "customer", employee_id: "", customer_id: "", period_type: "daily", start_date: today(), end_date: plusDays(today(), 1), start_time: new Date().toTimeString().slice(0, 5), end_time: new Date().toTimeString().slice(0, 5), authorized: false, discount: "", salary_deduct: false, installment: "", notes: "", attachments: [] };

const Row = ({ l, v, strong }) => (<div className={`flex justify-between ${strong ? "font-bold text-slate-900 text-base" : "text-slate-600"}`}><span>{l}</span><span className="tabular-nums">{v}</span></div>);

export const RentalContractDialog = ({ open, onOpenChange, employees, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [fleet, setFleet] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [qt, setQt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [custOpen, setCustOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(EMPTY); setQt(null);
    api.rentalFleet().then((r) => setFleet(r.filter((x) => x.status === "available" || x.manual_rented))).catch(() => {});
    api.rentalCustomers().then(setCustomers).catch(() => {});
  }, [open]);
  const renterOk = f.renter_type === "employee" ? f.employee_id : f.customer_id;
  useEffect(() => {
    if (!open || !f.fleet_id || !f.start_date || !f.end_date) { setQt(null); return; }
    const h = setTimeout(() => api.rentalQuote({ ...f, discount: Number(f.discount) || 0, installment: 0 }).then(setQt).catch(() => setQt(null)), 250);
    return () => clearTimeout(h);
  }, [open, f]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.fleet_id || !renterOk) { toast.warning("اختر المركبة والمستأجر"); return; }
    setBusy(true);
    try {
      const r = await api.createRentalContract({ ...f, discount: Number(f.discount) || 0, installment: Number(f.installment) || 0 });
      toast.success(`تم إنشاء العقد ${r.number}`); onOpenChange(false); onSaved(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر إنشاء العقد"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl font-body max-h-[92vh] overflow-y-auto" dir="rtl" data-testid="rental-contract-dialog">
        <DialogHeader><DialogTitle>عقد تأجير جديد</DialogTitle><DialogDescription>المبلغ يُحسب تلقائياً حسب المدة وسعر المركبة.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><Label>المركبة (المتاحة فقط)</Label>
          <select data-testid="rental-contract-fleet" value={f.fleet_id} onChange={set("fleet_id")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
            <option value="">اختر المركبة ({fleet.length})</option>
            {fleet.map((x) => <option key={x.id} value={x.id}>{`${x.identifier} — ${x.vtype} — يومي ${x.daily_rate || 0}`}</option>)}
          </select></div>
        <VehiclePhotosStrip photos={fleet.find((x) => x.id === f.fleet_id)?.photos || []} testid="rental-contract-photos" />
        <div className="flex gap-2">
          {[["customer", "عميل خارجي"], ["employee", "مندوب / موظف"]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`rental-contract-renter-${k}`} onClick={() => setF({ ...f, renter_type: k })}
              className={`flex-1 rounded-lg border py-2 text-sm font-semibold ${f.renter_type === k ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}>{l}</button>
          ))}
        </div>
        {f.renter_type === "customer" ? (
          <div className="flex gap-2 items-end">
            <div className="flex-1 space-y-1.5"><Label>العميل</Label>
              <select data-testid="rental-contract-customer" value={f.customer_id} onChange={set("customer_id")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">اختر العميل</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{`${c.name}${c.phone ? ` — ${c.phone}` : ""}`}</option>)}
              </select></div>
            <Button type="button" variant="outline" size="icon" data-testid="rental-contract-new-customer" onClick={() => setCustOpen(true)} title="عميل جديد"><Plus className="h-4 w-4" /></Button>
          </div>
        ) : (
          <div className="space-y-1.5"><Label>الموظف</Label>
            <select data-testid="rental-contract-employee" value={f.employee_id} onChange={set("employee_id")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">اختر الموظف</option>
              {employees.filter(isActiveEmp).map((e) => <option key={e.id} value={e.id}>{`${e.name} — ${e.branch || "—"}`}</option>)}
            </select></div>
        )}
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1.5"><Label>نوع المدة</Label>
            <select data-testid="rental-contract-period" value={f.period_type} onChange={set("period_type")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
              {PERIODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          <div className="space-y-1.5"><Label>من</Label><Input data-testid="rental-contract-start" type="date" dir="ltr" value={f.start_date} onChange={set("start_date")} /></div>
          <div className="space-y-1.5"><Label>إلى</Label><Input data-testid="rental-contract-end" type="date" dir="ltr" value={f.end_date} onChange={set("end_date")} /></div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div />
          <div className="space-y-1.5"><Label>وقت الاستلام</Label><input data-testid="rental-contract-start-time" type="time" dir="ltr" value={f.start_time} onChange={set("start_time")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" /></div>
          <div className="space-y-1.5"><Label>وقت الإرجاع</Label><input data-testid="rental-contract-end-time" type="time" dir="ltr" value={f.end_time} onChange={set("end_time")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" /></div>
        </div>
        <label data-testid="rental-contract-authorized" className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 cursor-pointer ${f.authorized ? "border-emerald-300 bg-emerald-50" : "border-amber-200 bg-amber-50/60"}`}>
          <div><div className={`text-sm font-semibold ${f.authorized ? "text-emerald-700" : "text-amber-700"}`}>تم التفويض (أبشر / نقل)</div>
            <div className="text-[11px] text-slate-500">تفويض المستأجر بقيادة المركبة خلال مدة العقد</div></div>
          <input type="checkbox" checked={f.authorized} onChange={(e) => setF({ ...f, authorized: e.target.checked })} className="h-4 w-4 accent-emerald-600" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5"><Label>خصم (ريال)</Label><Input data-testid="rental-contract-discount" type="number" dir="ltr" value={f.discount} onChange={set("discount")} /></div>
          <div className="space-y-1.5"><Label>ملاحظات</Label><Input data-testid="rental-contract-notes" value={f.notes} onChange={set("notes")} /></div>
        </div>
        <div className="space-y-1.5"><Label>ملفات العقد (اختياري)</Label>
          <RentalFilesField value={f.attachments} onChange={(v) => setF((p) => ({ ...p, attachments: v }))} testid="rental-contract-files" /></div>
        {f.renter_type === "employee" && (
          <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-violet-800" data-testid="rental-contract-salary-deduct">
              <input type="checkbox" checked={f.salary_deduct} onChange={(e) => setF({ ...f, salary_deduct: e.target.checked })} className="h-4 w-4 accent-violet-600" /> يُخصم الإيجار من الراتب
            </label>
            {f.salary_deduct && <Input data-testid="rental-contract-installment" type="number" dir="ltr" placeholder="القسط الشهري (فارغ = المبلغ كاملاً)" value={f.installment} onChange={set("installment")} />}
          </div>
        )}
        {qt && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-1 text-sm" data-testid="rental-contract-quote">
            <Row l={`المدة: ${qt.days} يوم = ${qt.units} × ${qt.rate}`} v={(qt.units * qt.rate).toLocaleString("en-US")} />
            {qt.discount > 0 && <Row l="الخصم" v={`- ${qt.discount}`} />}
            <Row l="المجموع قبل الضريبة" v={qt.subtotal.toLocaleString("en-US")} />
            {qt.vat > 0 && <Row l="ضريبة القيمة المضافة 15%" v={qt.vat.toLocaleString("en-US")} />}
            <Row l="الإجمالي" v={qt.total.toLocaleString("en-US")} strong />
            <p className="text-[11px] text-slate-400">التأخير بعد وقت الإرجاع بأكثر من ساعة يُحسب يوماً إضافياً</p>
            {qt.deposit > 0 && <Row l="التأمين المسترد" v={qt.deposit.toLocaleString("en-US")} />}
          </div>
        )}
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="rental-contract-save" disabled={busy} onClick={save} className="bg-blue-600 hover:bg-blue-700 gap-1.5">{busy && <Loader2 className="h-4 w-4 animate-spin" />} إنشاء العقد</Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
        <CustomerDialog open={custOpen} editing={null} onOpenChange={setCustOpen}
          onSaved={(c) => { setCustomers((p) => [c, ...p]); setF((p) => ({ ...p, customer_id: c.id })); }} />
      </DialogContent>
    </Dialog>
  );
};
